import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface CreateTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (name: string, key: string) => Promise<void>;
}

const DEFAULT_SYSTEM_VARIABLES = [
    { key: 'to', label: 'Primary Recipient', default_value: 'recipient@example.com', description: 'Target email address', required: true },
    { key: 'cc', label: 'Carbon Copy', default_value: 'cc@example.com', description: 'CC recipient addresses', required: false },
    { key: 'bcc', label: 'Blind Carbon Copy', default_value: 'bcc@example.com', description: 'BCC recipient addresses', required: false },
    { key: 'subject', label: 'Email Subject', default_value: 'Notification Update', description: 'Email subject text', required: true },
];

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
}) => {
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleNameChange = (val: string) => {
        setName(val);
        // Auto-slugify name to key if key hasn't been manually heavily customized
        const autoKey = val.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_');
        setKey(autoKey);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('Template name is required');
            return;
        }
        if (!key.trim()) {
            toast.error('Template key is required');
            return;
        }

        setIsSubmitting(true);
        try {
            await onConfirm(name.trim(), key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
            setName('');
            setKey('');
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    type="button"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3 text-(--primary)">
                    <div className="p-2.5 bg-sky-50 dark:bg-sky-950/50 rounded-lg text-(--primary)">
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold dark:text-white">Create Email Template</h3>
                        <p className="text-xs text-slate-500">Quickly create a template structure</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                    <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Template Display Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="e.g. Welcome Email"
                            className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-950"
                            autoFocus
                            required
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Template Key Slug <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={key}
                            onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                            placeholder="e.g. welcome_email"
                            className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 font-mono text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-950"
                            required
                        />
                    </div>

                    <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-lg border border-slate-100 dark:border-slate-800 space-y-1.5 text-xs">
                        <p className="font-semibold text-slate-600 dark:text-slate-400">Included Default Built-in Fields:</p>
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {DEFAULT_SYSTEM_VARIABLES.map(v => (
                                <span key={v.key} className="font-mono text-[11px] px-2 py-0.5 rounded bg-sky-50 dark:bg-sky-955 text-(--primary) border border-sky-100 dark:border-sky-900/60 font-semibold bg-sky-50 dark:bg-sky-950">
                                    {`{{${v.key}}}`}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !name.trim() || !key.trim()}
                            className="px-4 py-2 text-sm font-semibold bg-(--primary) hover:bg-(--primary-hover) disabled:opacity-50 text-white rounded-lg transition-colors shadow-sm"
                        >
                            {isSubmitting ? 'Creating...' : 'Create & Configure'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
